import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EditorView } from '@codemirror/view';
import { LeftRack, type RackSectionId } from './components/LeftRack';
import { ResizableSideRail } from './components/ResizableSideRail';
import { SampleStoreProvider } from './blocks/library/SampleStoreContext';
import { TransportDeck } from './components/TransportDeck';
import { PerformanceStrip } from './components/PerformanceStrip';
import { SourcePane, type EditorSelection } from './components/SourcePane';
import { CommandPalette, type Command } from './components/CommandPalette';
import { StatusBar } from './components/StatusBar';
import { MobilePedalboard } from './components/MobilePedalboard';
import { PwaUpdater } from './components/PwaUpdater';
import { isLearnPreviewId } from './learn/useLearnPreview';

// Modals are lazy-loaded — each one's bundle only ships when the user
// first opens it. Conditional rendering at the JSX call sites is what
// defers the import; if the lazy components were rendered eagerly with
// just an `open=false` prop they'd resolve at app boot.
const SettingsModal = lazy(() =>
  import('./components/SettingsModal').then((m) => ({ default: m.SettingsModal })),
);
const PublishSongModal = lazy(() =>
  import('./components/PublishSongModal').then((m) => ({ default: m.PublishSongModal })),
);
const TakesModal = lazy(() =>
  import('./components/TakesModal').then((m) => ({ default: m.TakesModal })),
);
const LearnModal = lazy(() =>
  import('./learn/LearnModal').then((m) => ({ default: m.LearnModal })),
);
const RecoverPasswordModal = lazy(() =>
  import('./auth/RecoverPasswordModal').then((m) => ({ default: m.RecoverPasswordModal })),
);
const GridEditorModalLazy = lazy(() =>
  import('./blocks/library/GridEditorModal').then((m) => ({ default: m.GridEditorModal })),
);
import { EditContext } from './blocks/editContext';
import { SoloContext } from './blocks/soloContext';
import { parseCode } from './blocks/parser';
import { parseCodeCachedSync } from './blocks/parserClient';
import { generateCode } from './blocks/codegen';
import { isolateForSolo, isSoloableId } from './blocks/solo';
import {
  setSoloGutterState,
  type SoloGutterEntry,
} from './components/codemirror/playableSoloGutter';
import { flashViewRange } from './components/codemirror/changeFlashExtension';
import {
  dispatchPlayingRanges,
  rangesEqual,
  type PlayingRange,
} from './components/codemirror/playingLinesExtension';
import { setTempo } from './blocks/tempo';
import type { BlockId, Song } from './blocks/types';
import { useLibrary } from './hooks/useLibrary';
import {
  useUnsavedGuard,
  UnsavedGuardProvider,
} from './hooks/useUnsavedGuard';
import { useAutosave } from './hooks/useAutosave';
import { UnsavedChangesModal } from './components/UnsavedChangesModal';
import { useSongEdit } from './hooks/useSongEdit';
import { useRecordings } from './hooks/useRecordings';
import { buildTransformCommands } from './transforms';
import { buildGridCommands } from './blocks/library/gridCommands';
import type { GridTarget } from './blocks/library/GridEditorModal';
import {
  useStrudel,
  useHapData,
  useNowRef,
  subscribeTick,
  getRecordingStream,
  hasRecordingTap,
  getAudioContext,
  getSampleStorage,
  getSound,
} from './strudel';
import type { StatusLevel } from './strudel';
import { extractSampleNames } from './strudel/extractSampleNames';
import { resolvePatchSamples } from './cloud/resolvePatchSamples';
import { WelcomePane, DEMO_CODE, type HomeLessonCard } from './components/WelcomePane';
import { useSession } from './auth/useSession';
import { DropOverlay } from './samples/ui/DropOverlay';
import { ChatPanel, type SeededPrompt } from './components/ChatPanel';
import {
  SharedSongBanner,
  type SharedBannerState,
} from './components/SharedSongBanner';
import { OnboardingTour } from './onboarding/OnboardingTour';
import { isOnboarded } from './onboarding/tourState';
import { parseShareId, buildShareUrl } from './share/route';
import { getSharedPatch, forkPatchToSong, recordPlayRequest } from './cloud';
import type { AgentBridge, TransportSnapshot } from './agent/bridge';
import {
  convertToWav,
  filenameFor,
  makeDecoder,
  triggerBrowserDownload,
} from './recording/download';

type Status = { message: string; level: StatusLevel };

// Length of a library sound audition. ~3 cycles at the preview tempo,
// long enough to hear the pattern, short enough that forgetting to
// stop doesn't disturb the user.
const PREVIEW_DURATION_MS = 1800;

// How long to wait after a code or solo change before re-evaluating
// while playing. Short enough to feel responsive, long enough that
// slider drags / held keys don't thrash the evaluator.
const HOT_SWAP_DEBOUNCE_MS = 250;

const SYNC_KEY = 'strudel-harness:sync-offset-ms';

// Library-rail section persistence. Lifted to App.tsx so handlers
// (openForge, etc.) can switch sections imperatively.
const SECTION_STORAGE_KEY = 'strudel-harness:rack-section';
const DEFAULT_SECTION: RackSectionId = 'vault';

const SONGS_PULSE_MS = 600;

// Voice-token extractor for the playing-line ticker. Mirrors the
// runtime voice resolution in useHaps.voiceOf: hap value is keyed by
// `s` (sample name) when present; otherwise note-bearing values fall
// back to the catch-all 'note'. So our static index buckets a
// statement under each `s("…")` first-token AND under 'note' if the
// statement uses note(/n( without any s() call.
const S_CALL_LITERAL = /\bs\(\s*"([^"\n]+)"/g;
const NOTE_CALL = /\b(?:note|n)\s*\(/;

function extractVoiceTokens(slice: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let hasSCall = false;
  S_CALL_LITERAL.lastIndex = 0;
  for (let m; (m = S_CALL_LITERAL.exec(slice)); ) {
    hasSCall = true;
    const payload = m[1] ?? '';
    for (const tok of payload.split(/\s+/)) {
      if (!tok || tok === '~' || tok === '-') continue;
      const name = tok.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)?.[0];
      if (!name) continue;
      if (seen.has(name)) continue;
      seen.add(name);
      out.push(name);
    }
  }
  if (!hasSCall && NOTE_CALL.test(slice)) {
    out.push('note');
  }
  return out;
}

// Curated lesson cards for the Home page — the first lesson of each
// of the first six modules. Hardcoded rather than derived from the
// full LESSONS catalog: importing the catalog into App.tsx would pull
// every lesson body into the main bundle (~30 KB gzip) just to read
// six titles, defeating the lazy-load on LearnModal. If a lesson id
// here ever drifts from the catalog, LearnModal's initialLessonId
// fallback (last-visited → first lesson) keeps the click safe.
const LESSON_CARDS: readonly HomeLessonCard[] = [
  {
    id: 'foundations.bpm',
    title: 'Tempo as feeling',
    moduleTitle: 'Foundations',
    accent: 'yellow',
    estMinutes: 4,
  },
  {
    id: 'mini.basics',
    title: 'Steps, rests, brackets',
    moduleTitle: 'Mini-notation',
    accent: 'yellow',
    estMinutes: 4,
  },
  {
    id: 'transforms.rev-palindrome',
    title: 'Reverse and palindrome',
    moduleTitle: 'Pattern transforms',
    accent: 'cyan',
    estMinutes: 4,
  },
  {
    id: 'conditionals.every-when',
    title: 'every and whenmod',
    moduleTitle: 'Conditionals',
    accent: 'fuchsia',
    estMinutes: 5,
  },
  {
    id: 'structure.sections',
    title: 'What each section does',
    moduleTitle: 'Structure',
    accent: 'emerald',
    estMinutes: 7,
  },
  {
    id: 'expression.dynamics',
    title: 'Automation as performance',
    moduleTitle: 'Expression',
    accent: 'cyan',
    estMinutes: 6,
  },
];

function readPersistedSection(): RackSectionId {
  if (typeof window === 'undefined') return DEFAULT_SECTION;
  try {
    const raw = window.localStorage.getItem(SECTION_STORAGE_KEY);
    if (
      raw === 'songs' ||
      raw === 'vault' ||
      raw === 'mine' ||
      raw === 'community'
    ) {
      return raw;
    }
  } catch {
    // localStorage may throw in private mode / quota — fall through.
  }
  return DEFAULT_SECTION;
}

function persistSection(id: RackSectionId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SECTION_STORAGE_KEY, id);
  } catch {
    // Persistence is best-effort.
  }
}

// Visualizer's original query window — kept identical so hap motion
// in the PerformanceStrip matches the previous Visualizer behaviour.
const HAP_LOOKBEHIND = 1;
const HAP_LOOKAHEAD = 2;

type PlayCode = { ok: true; code: string } | { ok: false; reason: string };

const UNPLAYABLE: PlayCode = {
  ok: false,
  reason: 'Song has no playable top-level expression — add stack(…) or a bare voice at the end.',
};

// Strudel's transpiler requires the last body statement to be an
// ExpressionStatement (something it can wrap in `return …`). In our
// model, every kind except `const` renders to one — `setcpm`/`setcps`
// are bare calls, `expression` obviously is. A trailing `const` binding
// with no play expression below it is the only shape that breaks.
// Empty song is fine; Strudel falls back to silence.
function hasPlayableTrailingStatement(song: Song): boolean {
  for (let i = song.statements.length - 1; i >= 0; i--) {
    const s = song.statements[i];
    if (!s || s.kind === 'comment') continue;
    return s.kind !== 'const';
  }
  return true;
}

function readStoredSyncOffset(): number {
  try {
    const raw = localStorage.getItem(SYNC_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function App() {
  // Active surface — Home is a first-class destination now, not a
  // first-run-only screen. Cold start always lands on Home so the
  // user sees their Recent / Favourites / Lessons strips before
  // dropping into the editor. Any selection or CTA flips view to
  // 'editor'; the Home button in TransportDeck flips it back.
  const [view, setView] = useState<'home' | 'editor'>('home');
  // goEditor / goHome are defined later, after the unsaved-changes
  // guard is set up — switching views with a dirty buffer prompts.

  // App-level session hook: drives the global recovery modal mount.
  // Recovery is a user-level concern, not a panel-level one — landing
  // on a recovery URL while the auth panel is collapsed should still
  // surface the modal.
  const {
    session,
    loading: sessionLoading,
    recoveryMode,
    exitRecoveryMode,
  } = useSession();

  // Library: cloud-first. Songs live in Supabase `patches`; the
  // editor buffer (editorTitle/editorCode below) is the local
  // working draft, persisted only when the user (or autosave) saves.
  // Anonymous users see DEFAULT_SONGS read-only. Existing
  // localStorage libraries from before this refactor migrate up
  // automatically on first sign-in.
  const songs = useLibrary({ session, sessionLoading });

  const [status, setStatus] = useState<Status>({ message: 'Booting…', level: '' });
  const onStatus = useCallback((message: string, level: StatusLevel = '') => {
    setStatus({ message, level });
  }, []);

  const strudel = useStrudel({ onStatus });

  // All play / restart paths route through these wrappers so that any
  // s("name") referencing a cloud sample we haven't yet registered with
  // Strudel gets resolved (and lazy-URL-registered) *before* the
  // pattern evaluates. Without this, missing samples spam the
  // window.error handler one error per voice trigger per cycle.
  // Best-effort: if the lookup or registration fails, we still call
  // play/restart — the original "sound not registered" error then
  // surfaces honestly for genuinely-unknown names.
  const ensureSamplesRegistered = useCallback(async (code: string) => {
    try {
      const names = extractSampleNames(code);
      const missing = names.filter((n) => !getSound(n));
      if (missing.length > 0) {
        await resolvePatchSamples(missing);
      }
    } catch (e) {
      console.error('[play] sample resolve failed', e);
    }
  }, []);
  const playWithResolve = useCallback(
    async (code: string): Promise<void> => {
      await ensureSamplesRegistered(code);
      strudel.play(code);
    },
    [ensureSamplesRegistered, strudel.play],
  );
  const restartWithResolve = useCallback(
    async (code: string): Promise<void> => {
      await ensureSamplesRegistered(code);
      strudel.restart(code);
    },
    [ensureSamplesRegistered, strudel.restart],
  );

  const [editorTitle, setEditorTitle] = useState('');
  const [editorCode, setEditorCode] = useState('');

  // Whether the welcome pane's own demo button is currently driving
  // the engine. We track this separately from `strudel.phase` so the
  // CTA's playing state survives a brief 'paused'/'ready' transition
  // and so other playback paths (preview, regular play) don't make
  // the welcome's button mis-render.
  const [welcomeDemoPlaying, setWelcomeDemoPlaying] = useState(false);

  // First-run onboarding. `onboarded` swaps WelcomePane's primary CTA
  // (Jump in vs Hear the demo); `tourActive` keeps the coachmark tour
  // mounted across home ↔ editor swaps until it completes or is
  // dismissed. `agentSeed` pipes the tour's suggested prompt into
  // ChatPanel's send().
  const [onboarded, setOnboarded] = useState<boolean>(() => isOnboarded());
  const [tourActive, setTourActive] = useState(false);
  const [agentSeed, setAgentSeed] = useState<SeededPrompt | null>(null);
  const agentSeedIdRef = useRef(0);

  // Shared-song landing. Non-null while the app is opened on a
  // `/s/<id>` link — drives the SharedSongBanner above the editor. The
  // shared code loads into the editor buffer as an unsaved draft.
  const [sharedBanner, setSharedBanner] = useState<SharedBannerState | null>(
    null,
  );
  const [sharedForking, setSharedForking] = useState(false);

  // Left rack overlay on mobile. Desktop ignores this state.
  const [rackOpen, setRackOpen] = useState(false);

  // Desktop-only: rail-level collapse to a 44px spine. The user's
  // expanded width is preserved by ResizableSideRail; toggling
  // restores it. Mobile ignores this — the drawer pattern handles it.
  const [rackCollapsed, setRackCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('strudel-harness:rack-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const handleToggleRackCollapsed = useCallback(() => {
    setRackCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(
          'strudel-harness:rack-collapsed',
          next ? '1' : '0',
        );
      } catch {
        // Persistence is best-effort.
      }
      return next;
    });
  }, []);

  // Which library section is expanded inside the rail. Lifted up so
  // handleOpenForge can switch to MINE imperatively.
  const [expandedSection, setExpandedSection] = useState<RackSectionId>(
    readPersistedSection,
  );
  const handleSwitchSection = useCallback((id: RackSectionId) => {
    setExpandedSection(id);
    persistSection(id);
  }, []);

  // Solo state lives here (not inside useSongEdit) so the play pipeline
  // can swap in an isolated code string. Ephemeral — not persisted
  // with the song, and cleared whenever the active song changes.
  const [soloId, setSoloId] = useState<BlockId | null>(null);

  // CodeMirror view, captured from SourcePane. We keep both a ref (for
  // callback paths that don't want to wait for a render cycle — e.g.
  // handleSelectSourceRange inside a rAF) and a state mirror (so the
  // solo-gutter dispatch effect below can fire after the view first
  // mounts). One writer, two readers.
  const viewRef = useRef<EditorView | null>(null);
  const [editorView, setEditorView] = useState<EditorView | null>(null);

  // Sound-library preview. Strudel is a process-wide singleton — so a
  // preview shares the same audio stream as main playback. We track
  // which catalog entry is currently auditioning so the library can
  // render a stop icon on it, and hold a timeout handle so we can
  // cancel the auto-stop when the user triggers a "real" play.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewTimerRef = useRef<number | null>(null);

  // Last code string handed to the engine. Shared across every code
  // path that calls `strudel.play(...)` so the hot-swap effect can
  // dedupe against a manual Play or a solo transition that just fired
  // with the same code 250ms earlier.
  const lastPlayedCodeRef = useRef<string | null>(null);

  const cancelPreviewTimer = useCallback(() => {
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    setPreviewId(null);
  }, []);

  // Sync-offset was formerly owned by Visualizer. Lifted so the
  // TransportDeck phasor and the PerformanceStrip voice traces consume
  // the same rAF now-ref (sourced from `useNowRef(syncOffsetMs)`). Single
  // source of truth avoids two rAF loops competing or drifting when
  // the user nudges the sync slider.
  const [syncOffsetMs, setSyncOffsetState] = useState<number>(() =>
    readStoredSyncOffset(),
  );
  const updateSyncOffset = useCallback((next: number) => {
    setSyncOffsetState(next);
    try {
      localStorage.setItem(SYNC_KEY, String(next));
    } catch {
      // Private browsing / quota — in-memory state still works.
    }
  }, []);

  // Shared engine telemetry. `refs` holds fast-changing data (haps,
  // cycle, cps, pollNow, started) — read from a ticker subscription
  // in consumers, no React state churn. `structural` is React state
  // that updates only when the voice order or pattern epoch changes
  // (rare), which is when the PerformanceStrip needs to rebuild its
  // lane list. App re-renders from this hook only on those structural
  // changes — not on every 12fps poll.
  const { refs: hapRefs, structural: hapStructural } = useHapData({
    queryBehind: HAP_LOOKBEHIND + 1,
    queryAhead: HAP_LOOKAHEAD + 2,
    fps: 12,
  });
  const nowRef = useNowRef(syncOffsetMs);

  const handleSelectSourceRange = useCallback(
    (range: { from: number; to: number }) => {
      requestAnimationFrame(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
          selection: { anchor: range.from, head: range.to },
          scrollIntoView: true,
        });
        view.focus();
      });
    },
    [],
  );

  // Parse / history / undo-redo / target tracking / solo derivation
  // for the song. EditContext + SoloContext wrap the layout so the
  // SoundLibrary in the LeftRack and the agent bridge can both apply
  // edits through the same pipeline.
  const songEdit = useSongEdit({
    code: editorCode,
    onCodeChange: setEditorCode,
    soloId,
    onSoloChange: setSoloId,
    onSelectSourceRange: handleSelectSourceRange,
  });

  // Load the selected song into the buffer whenever it changes.
  // Clear ephemeral solo state on song-switch — soloId is a BlockId
  // which doesn't survive reparses across songs.
  useEffect(() => {
    if (songs.activeSong) {
      setEditorTitle(songs.activeSong.title);
      setEditorCode(songs.activeSong.code);
      setSoloId(null);
      cancelPreviewTimer();
    }
  }, [songs.activeSong, cancelPreviewTimer]);

  // Dirty = the editor buffer differs from the saved song it came
  // from. For a brand-new (unsaved) song, anything non-empty counts
  // as dirty so the Save button lights up. Memoised on the inputs
  // because TransportDeck is downstream of App's 0-fps re-render
  // budget — a string compare once per render is trivial either way.
  const dirty = useMemo(() => {
    if (!songs.activeSong) {
      return editorTitle.length > 0 || editorCode.length > 0;
    }
    return (
      songs.activeSong.title !== editorTitle ||
      songs.activeSong.code !== editorCode
    );
  }, [songs.activeSong, editorTitle, editorCode]);

  // Settings modal — declared up here because saveCurrent below opens
  // it for anon users who try to save (see also handleOpenSettings).
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Save the editor buffer to the current cloud song. Returns false
  // if the save was deferred (anon user routed to sign-in) so the
  // unsaved-changes guard can keep the buffer in place rather than
  // proceeding with a navigation that would drop it.
  const saveCurrent = useCallback((): boolean => {
    if (!session) {
      setStatus({
        message: 'Sign in to save your work — opening Settings.',
        level: 'warn',
      });
      setSettingsOpen(true);
      return false;
    }
    songs.save(editorTitle, editorCode);
    return true;
  }, [session, editorTitle, editorCode, songs]);

  // Unsaved-changes guard: navigation actions go through guardThen so
  // a dirty buffer prompts before being silently discarded.
  const { guardThen, prompt: unsavedPrompt } = useUnsavedGuard({
    dirty,
    save: saveCurrent,
  });

  // Autosave: opt-in via Settings. Fires saveCurrent once the user
  // has been idle for `idleMs` while the buffer is dirty.
  const autosave = useAutosave({
    dirty,
    title: editorTitle,
    code: editorCode,
    save: songs.save,
  });

  // Surface autosave transitions in the status bar so the user can
  // trust it. Only react to phase changes — not to the periodic
  // `at` updates within 'saved' — to avoid stomping on other status
  // messages.
  const autosavePhase = autosave.status.phase;
  useEffect(() => {
    if (autosavePhase === 'saving') {
      setStatus({ message: 'Autosaving…', level: '' });
    } else if (autosavePhase === 'saved') {
      setStatus({ message: 'Saved.', level: 'ok' });
    }
  }, [autosavePhase]);

  // View switches: guarded so a dirty buffer can't be silently dropped.
  const goEditorRaw = useCallback(() => setView('editor'), []);
  const goHomeRaw = useCallback(() => setView('home'), []);
  const goEditor = useMemo(() => guardThen(goEditorRaw), [guardThen, goEditorRaw]);
  const goHome = useMemo(() => guardThen(goHomeRaw), [guardThen, goHomeRaw]);

  const handleOpenRack = useCallback(() => setRackOpen(true), []);
  const handleCloseRack = useCallback(() => setRackOpen(false), []);

  const handleSetView = useCallback((view: EditorView | null) => {
    viewRef.current = view;
    setEditorView(view);
  }, []);

  // Solo a block via its BlockId with radio semantics — click the same
  // id twice to clear. Stable identity so the CodeMirror extension can
  // hold onto it without reconfiguring. Wired up below via a state-
  // effect dispatch rather than by rebuilding extensions.
  const handleGutterSoloToggle = useCallback((id: string) => {
    setSoloId((prev) => (prev === id ? null : id));
  }, []);

  // Top-level const bindings whose RHS produces audio — these get a ▶
  // button in the source gutter. Excludes `customFunction` RHSs (opaque
  // arrow-function bindings never produce audio on their own). Recomputed
  // only when the parsed song changes, not on every keystroke while the
  // async parse is in flight.
  const soloGutterEntries = useMemo<SoloGutterEntry[]>(() => {
    const song = songEdit.song;
    if (!song) return [];
    const out: SoloGutterEntry[] = [];
    for (const stmt of song.statements) {
      if (stmt.kind !== 'const') continue;
      if (stmt.expr.kind === 'customFunction') continue;
      if (!stmt.sourceRange) continue;
      if (!isSoloableId(song, stmt.id)) continue;
      out.push({ from: stmt.sourceRange.from, blockId: stmt.id });
    }
    return out;
  }, [songEdit.song]);

  // Push gutter state into CodeMirror on every change. We dispatch a
  // StateEffect rather than rebuilding extensions: cheaper, and doesn't
  // perturb editor state like selection/scroll position.
  useEffect(() => {
    if (!editorView) return;
    editorView.dispatch({
      effects: setSoloGutterState.of({
        entries: soloGutterEntries,
        soloId,
        onToggle: handleGutterSoloToggle,
      }),
    });
  }, [editorView, soloGutterEntries, soloId, handleGutterSoloToggle]);

  // Voice → statement source-range index. Used by the playing-line
  // ticker to map a currently-firing hap (which only knows its `voice`
  // string) back to the line(s) that emitted it. Heuristic, regex-based
  // — we extract `s("…")` payloads (canonical voice tokens) and bucket
  // statements that use `note(`/`n(` without an `s` call under the
  // catch-all 'note' voice (matching voiceOf in useHaps). Safe to be
  // approximate: false positives just mean the line glows on cycles
  // where its sibling sounded; false negatives mean a line stays dark
  // when it shouldn't. Real-world Strudel patterns parse cleanly here.
  const voiceRangesMap = useMemo(() => {
    const map = new Map<string, PlayingRange[]>();
    const song = songEdit.song;
    if (!song) return map;
    for (const stmt of song.statements) {
      if (stmt.kind !== 'const') continue;
      if (!stmt.sourceRange) continue;
      const range: PlayingRange = {
        from: stmt.sourceRange.from,
        to: stmt.sourceRange.to,
      };
      const slice = editorCode.slice(range.from, range.to);
      const voices = extractVoiceTokens(slice);
      for (const v of voices) {
        const list = map.get(v);
        if (list) list.push(range);
        else map.set(v, [range]);
      }
    }
    return map;
  }, [songEdit.song, editorCode]);

  // Per-frame: derive the set of source ranges currently producing
  // sound (any hap with `begin <= now < end` whose voice maps back to a
  // statement). Push the changed-only diff into the editor so we don't
  // re-dispatch identical sets at 60fps.
  const lastPlayingRef = useRef<PlayingRange[]>([]);
  useEffect(() => {
    if (!editorView) return;
    const tick = (): void => {
      const now = nowRef.current;
      const haps = hapRefs.haps.current;
      const started = hapRefs.started.current;
      let next: PlayingRange[] = [];
      if (started && haps.length > 0) {
        const seen = new Set<string>();
        for (const h of haps) {
          if (h.begin > now || h.end <= now) continue;
          const ranges = voiceRangesMap.get(h.voice);
          if (!ranges) continue;
          for (const r of ranges) {
            const key = r.from + ':' + r.to;
            if (seen.has(key)) continue;
            seen.add(key);
            next.push(r);
          }
        }
        next.sort((a, b) => a.from - b.from);
      }
      if (rangesEqual(lastPlayingRef.current, next)) return;
      lastPlayingRef.current = next;
      dispatchPlayingRanges(editorView, next);
    };
    return subscribeTick(tick);
  }, [editorView, voiceRangesMap, hapRefs, nowRef]);

  // Returns the code to feed into the player. When a block is soloed,
  // parse the editor buffer and reduce it to just the soloed subtree
  // plus its deps. Fallback to the raw code on any parse failure so
  // Play never silently breaks — the user gets Strudel's own error.
  //
  // Critical: solo isolation reads from `songEdit.song` (the canonical
  // parsed Song that produced the gutter's soloId) rather than from a
  // re-parse. Every fresh `parseCode` call mints new nanoid ids, and
  // the parserClient cache is best-effort — when it misses (LRU
  // eviction, in-flight async parse), a sync fallback parse would
  // mismatch the captured soloId, `findSoloTarget` would return null,
  // and `isolateForSolo` would silently return the full song,
  // surfacing as "inline ▶ plays everything".
  const resolvePlayCode = useCallback((): PlayCode => {
    if (soloId) {
      const song = songEdit.song;
      if (!song) return { ok: true, code: editorCode };
      const isolated = isolateForSolo(song, soloId);
      if (!hasPlayableTrailingStatement(isolated)) return UNPLAYABLE;
      return { ok: true, code: generateCode(isolated) };
    }

    const cached = parseCodeCachedSync(editorCode);
    const parsed = cached ?? parseCode(editorCode);

    if (parsed.ok && !hasPlayableTrailingStatement(parsed.song)) {
      return UNPLAYABLE;
    }

    return { ok: true, code: editorCode };
  }, [editorCode, soloId, songEdit.song]);

  const handlePlay = useCallback(async () => {
    cancelPreviewTimer();
    const resolved = resolvePlayCode();
    if (!resolved.ok) {
      setStatus({ message: resolved.reason, level: 'err' });
      return;
    }
    try {
      await playWithResolve(resolved.code);
      lastPlayedCodeRef.current = resolved.code;
      setStatus({
        message: soloId
          ? 'Playing solo — edits are live.'
          : 'Playing — edits are live.',
        level: 'ok',
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ message: 'Play error: ' + msg, level: 'err' });
      console.error(e);
    }
  }, [cancelPreviewTimer, resolvePlayCode, soloId, playWithResolve]);

  const handleStop = useCallback(() => {
    cancelPreviewTimer();
    strudel.stop();
    setStatus({ message: 'Stopped.', level: '' });
  }, [cancelPreviewTimer, strudel.stop]);

  // Restart: hot-swap aligns pattern changes to the next cycle boundary
  // and never resets the cyclist's cycle counter, so edits during
  // playback are heard "mid-song". `strudel.restart` halts the cyclist,
  // calls scheduler.setCycle(0), then re-evaluates — without that
  // setCycle call, arrange() / firstOf() / every() pick up where they
  // left off instead of replaying from the top.
  const handleRestart = useCallback(async () => {
    cancelPreviewTimer();
    const resolved = resolvePlayCode();
    if (!resolved.ok) {
      setStatus({ message: resolved.reason, level: 'err' });
      return;
    }
    try {
      await restartWithResolve(resolved.code);
      lastPlayedCodeRef.current = resolved.code;
      setStatus({ message: 'Restarted from cycle 0.', level: 'ok' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ message: 'Restart error: ' + msg, level: 'err' });
      console.error(e);
    }
  }, [cancelPreviewTimer, resolvePlayCode, restartWithResolve]);

  const handlePauseToggle = useCallback(() => {
    cancelPreviewTimer();
    if (strudel.phase === 'paused') {
      strudel.resume();
      setStatus({ message: 'Resumed.', level: 'ok' });
    } else {
      strudel.pause();
      setStatus({ message: 'Paused.', level: '' });
    }
  }, [cancelPreviewTimer, strudel.phase, strudel.pause, strudel.resume]);

  // Start previewing a library entry. Replaces whatever's currently
  // playing (audio engine is a singleton). Vault previews schedule an
  // auto-stop so a click doesn't run forever; Learn previews loop until
  // the user clicks Stop, because the lesson UX is "sit with the
  // pattern" rather than "hear it once".
  const handleStartPreview = useCallback(
    async (id: string, code: string, label: string, durationMs?: number) => {
      if (previewTimerRef.current !== null) {
        window.clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
      try {
        await playWithResolve(code);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatus({ message: 'Preview error: ' + msg, level: 'err' });
        setPreviewId(null);
        return;
      }
      setPreviewId(id);
      const isLearn = isLearnPreviewId(id);
      setStatus({
        message: isLearn ? `Looping ${label} — click ■ Stop to halt.` : `Previewing ${label}…`,
        level: '',
      });
      if (isLearn) return;
      previewTimerRef.current = window.setTimeout(() => {
        previewTimerRef.current = null;
        setPreviewId(null);
        strudel.stop();
        setStatus({ message: 'Preview ended.', level: '' });
      }, durationMs ?? PREVIEW_DURATION_MS);
    },
    [playWithResolve, strudel.stop],
  );

  const handleStopPreview = useCallback(() => {
    cancelPreviewTimer();
    strudel.stop();
    setStatus({ message: 'Preview stopped.', level: '' });
  }, [cancelPreviewTimer, strudel.stop]);

  useEffect(() => {
    return () => {
      if (previewTimerRef.current !== null) {
        window.clearTimeout(previewTimerRef.current);
      }
    };
  }, []);

  const canPreview =
    strudel.phase === 'ready' ||
    strudel.phase === 'playing' ||
    strudel.phase === 'paused';

  const handleSave = useCallback(() => {
    songs.save(editorTitle, editorCode);
    setStatus({ message: 'Saved.', level: 'ok' });
  }, [editorTitle, editorCode, songs.save]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const handleClosePalette = useCallback(() => setPaletteOpen(false), []);
  const [learnOpen, setLearnOpen] = useState(false);
  // Lesson id to land on next time the modal opens. Cleared on close.
  // Lets a click on a Home lesson card open Learn directly at that
  // lesson rather than wherever the modal was last left.
  const [pendingLessonId, setPendingLessonId] = useState<string | undefined>(undefined);
  const handleOpenLearn = useCallback(() => {
    setPendingLessonId(undefined);
    setLearnOpen(true);
  }, []);
  const handleCloseLearn = useCallback(() => {
    setLearnOpen(false);
    setPendingLessonId(undefined);
  }, []);
  // Pulses the rail's Songs LED briefly after a Learn snippet lands
  // in the library. Auto-clears after SONGS_PULSE_MS so a single send
  // produces one flash, not a stuck animation.
  const [songsPulse, setSongsPulse] = useState(false);
  const songsPulseTimerRef = useRef<number | null>(null);
  const triggerSongsPulse = useCallback(() => {
    if (songsPulseTimerRef.current !== null) {
      window.clearTimeout(songsPulseTimerRef.current);
    }
    setSongsPulse(true);
    songsPulseTimerRef.current = window.setTimeout(() => {
      setSongsPulse(false);
      songsPulseTimerRef.current = null;
    }, SONGS_PULSE_MS);
  }, []);
  useEffect(() => {
    return () => {
      if (songsPulseTimerRef.current !== null) {
        window.clearTimeout(songsPulseTimerRef.current);
      }
    };
  }, []);
  const [takesOpen, setTakesOpen] = useState(false);
  const handleOpenTakes = useCallback(() => setTakesOpen(true), []);
  const handleCloseTakes = useCallback(() => setTakesOpen(false), []);
  const [gridTarget, setGridTarget] = useState<GridTarget | null>(null);
  const handleCloseGrid = useCallback(() => setGridTarget(null), []);
  const [publishTarget, setPublishTarget] = useState<{
    /** Captured at open-time so a song-switch mid-publish doesn't
     *  stamp the cloud id onto the wrong local song. */
    songId: string;
    title: string;
    code: string;
    cloudPatchId?: string;
  } | null>(null);
  const handleClosePublish = useCallback(() => setPublishTarget(null), []);
  // Forge mode lives inside the MINE section of the left rail. Opening
  // the Forge expands the rail (if collapsed), switches MINE to be the
  // active section, and flips MINE's body from list to forge mode.
  const [forgeActive, setForgeActive] = useState(false);
  const handleOpenForge = useCallback(() => {
    setRackCollapsed((wasCollapsed) => {
      if (wasCollapsed) {
        try {
          localStorage.setItem('strudel-harness:rack-collapsed', '0');
        } catch {
          // Persistence is best-effort.
        }
      }
      return false;
    });
    setExpandedSection('mine');
    persistSection('mine');
    setForgeActive(true);
  }, []);
  const handleForgeBack = useCallback(() => setForgeActive(false), []);
  const recordings = useRecordings();
  const handleToggleRecord = useCallback(() => {
    if (
      recordings.recorderState === 'recording' ||
      recordings.recorderState === 'paused'
    ) {
      recordings.stop();
      setStatus({ message: 'Recording stopped — take added.', level: 'ok' });
      return;
    }
    const stream = getRecordingStream();
    if (!stream) {
      setStatus({
        message: 'Recording unavailable — press Play first to boot the engine.',
        level: 'warn',
      });
      return;
    }
    recordings.start(stream);
    setStatus({ message: 'Recording audio output…', level: 'ok' });
  }, [recordings]);
  const handleDownloadTake = useCallback(
    async (takeId: string, format: 'wav' | 'raw') => {
      const take = recordings.takes.find((t) => t.id === takeId);
      if (!take) return;
      if (format === 'raw') {
        const extension = take.mimeType.includes('mp4') ? 'mp4' : 'webm';
        triggerBrowserDownload(take.blob, filenameFor(take.name, extension));
        return;
      }
      const ctx = getAudioContext();
      if (!ctx) {
        setStatus({
          message: 'Cannot export: audio engine has not booted.',
          level: 'err',
        });
        return;
      }
      try {
        const wavBlob = await convertToWav(take.blob, makeDecoder(ctx));
        triggerBrowserDownload(wavBlob, filenameFor(take.name, 'wav'));
        setStatus({ message: `Downloaded ${take.name}.wav`, level: 'ok' });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatus({ message: 'WAV export failed: ' + msg, level: 'err' });
      }
    },
    [recordings.takes],
  );
  const handleClearTakes = useCallback(() => {
    if (recordings.takes.length === 0) return;
    if (!confirm(`Delete all ${recordings.takes.length} takes in this session?`)) return;
    recordings.clearAll();
    setStatus({ message: 'Takes cleared.', level: 'ok' });
  }, [recordings]);
  const [editorSelection, setEditorSelection] = useState<EditorSelection | null>(
    null,
  );

  const handleNew = useCallback(() => {
    guardThen(() => {
      cancelPreviewTimer();
      strudel.stop();
      songs.prepareNew();
      setEditorTitle('');
      setEditorCode('');
      setSoloId(null);
      setStatus({
        message: 'New song — type Strudel or ask the agent to scaffold one.',
        level: 'ok',
      });
    })();
  }, [songs, strudel, cancelPreviewTimer, guardThen]);

  // Save-as-song mints a permanent new library entry. Mirrors
  // handleForked rather than handleNew — handleNew clears the buffer,
  // which would defeat the purpose. insertSong allocates a fresh uuid,
  // so two clicks on the same `send` node create two distinct rows.
  const handleLearnSaveAsSong = useCallback(
    (title: string, code: string) => {
      cancelPreviewTimer();
      strudel.stop();
      setWelcomeDemoPlaying(false);
      songs.insertSong(title, code);
      setEditorTitle(title);
      setEditorCode(code);
      setSoloId(null);
      goEditor();
      triggerSongsPulse();
      setStatus({ message: `Saved "${title}" to your songs.`, level: 'ok' });
    },
    [songs, strudel, cancelPreviewTimer, goEditor, triggerSongsPulse],
  );

  // Home page derived state. Recent = top-N by modifiedAt DESC;
  // Favourites = anything starred. Both capped so the strips don't
  // grow unbounded — overflow just isn't shown on Home, the user
  // navigates via the rack for full-library access.
  const recentSongs = useMemo(() => {
    return songs.songs
      .slice()
      .sort(
        (a, b) =>
          (b.modifiedAt ?? 0) - (a.modifiedAt ?? 0) ||
          a.title.localeCompare(b.title),
      )
      .slice(0, 6);
  }, [songs.songs]);
  const favouriteSongs = useMemo(
    () => songs.songs.filter((s) => s.favourite),
    [songs.songs],
  );

  // Open a library song. Audio gets stopped first — landing on a new
  // song mid-playback would either keep playing the old pattern
  // (confusing) or hot-swap into the new one without user intent (still
  // confusing). Better to make the user press play. Switches to the
  // editor view so a selection from Home doesn't leave WelcomePane up.
  const handleHomeOpenSong = useCallback(
    (id: string) => {
      guardThen(() => {
        cancelPreviewTimer();
        strudel.stop();
        setWelcomeDemoPlaying(false);
        songs.select(id);
        setView('editor');
      })();
    },
    [songs, strudel, cancelPreviewTimer, guardThen],
  );

  // Open the Learn modal at a specific lesson. Sets pendingLessonId
  // first so the modal's mount-time initializer reads it before
  // falling back to last-visited or the first lesson.
  const handleHomeOpenLesson = useCallback((lessonId: string) => {
    setPendingLessonId(lessonId);
    setLearnOpen(true);
  }, []);

  // Welcome-pane action handlers. Each leaves the user in a sensible
  // state and dismisses the landing card (except for Hear/Stop, which
  // are dwell actions — the user may want to listen before exiting).
  const handleWelcomeHearDemo = useCallback(async () => {
    cancelPreviewTimer();
    try {
      await playWithResolve(DEMO_CODE);
      lastPlayedCodeRef.current = DEMO_CODE;
      setWelcomeDemoPlaying(true);
      setStatus({ message: 'Demo playing — 4 bars on loop.', level: 'ok' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ message: 'Demo error: ' + msg, level: 'err' });
    }
  }, [cancelPreviewTimer, playWithResolve]);

  const handleWelcomeStopDemo = useCallback(() => {
    strudel.stop();
    setWelcomeDemoPlaying(false);
    setStatus({ message: 'Demo stopped.', level: '' });
  }, [strudel.stop]);

  // Loading the demo into the editor leaves it as an unsaved buffer
  // (prepareNew clears activeId) so a casual exploration doesn't silently
  // overwrite a default song. Audio keeps playing if it was — the user
  // can immediately edit while listening.
  const handleWelcomeOpenDemo = useCallback(() => {
    guardThen(() => {
      songs.prepareNew();
      setEditorTitle('Demo');
      setEditorCode(DEMO_CODE);
      setSoloId(null);
      // The audio that was the "welcome demo" is now backing editor
      // content one-to-one — flip the flag off so hot-swap re-engages
      // and the user's first edit is heard. The dedupe in the hot-swap
      // (next.code === lastPlayedCodeRef) prevents a re-eval glitch.
      setWelcomeDemoPlaying(false);
      setView('editor');
      setStatus({
        message: 'Demo loaded — edit anywhere; changes are heard live.',
        level: 'ok',
      });
    })();
  }, [songs, guardThen]);

  // First-run "Jump in": one gesture composes hear-demo (the required
  // user click that boots audio) with open-in-editor (hot-swap armed,
  // playback preserved via the lastPlayedCodeRef dedupe), then arms
  // the coachmark tour on the now-playing, now-editable buffer.
  const handleWelcomeJumpIn = useCallback(async () => {
    await handleWelcomeHearDemo();
    handleWelcomeOpenDemo();
    setTourActive(true);
  }, [handleWelcomeHearDemo, handleWelcomeOpenDemo]);

  const handleSeedAgentPrompt = useCallback((prompt: string) => {
    agentSeedIdRef.current += 1;
    setAgentSeed({ id: agentSeedIdRef.current, prompt });
  }, []);

  // Fired once when the tour completes or is dismissed. The tour has
  // already persisted the onboarded flag; mirroring it into state
  // flips the welcome CTA back for the rest of the session.
  const handleTourFinished = useCallback(() => {
    setOnboarded(true);
    setTourActive(false);
  }, []);

  const handleWelcomeStartBlank = useCallback(() => {
    setWelcomeDemoPlaying(false);
    handleNew();
    goEditor();
  }, [handleNew, goEditor]);

  const handleWelcomeBrowseCommunity = useCallback(() => {
    guardThen(() => {
      // Clear the editor as well as stopping the demo. Without this, the
      // user lands on the community section but sees DEFAULT_SONGS[0] in
      // the editor (the activeSong-watcher pre-populates it on mount) —
      // disorienting when their stated intent is "show me what others
      // have made". prepareNew() also nulls activeId so the cleared
      // buffer counts as an unsaved draft, not a silent overwrite.
      setWelcomeDemoPlaying(false);
      cancelPreviewTimer();
      strudel.stop();
      songs.prepareNew();
      setEditorTitle('');
      setEditorCode('');
      setSoloId(null);
      setExpandedSection('community');
      persistSection('community');
      setView('editor');
    })();
  }, [songs, strudel, cancelPreviewTimer, guardThen]);

  const handleOpenSettings = useCallback(() => setSettingsOpen(true), []);
  const handleCloseSettings = useCallback(() => setSettingsOpen(false), []);

  const handleOpenPublish = useCallback(() => {
    if (!songs.activeSong) return;
    setPublishTarget({
      songId: songs.activeSong.id,
      title: editorTitle || songs.activeSong.title,
      code: editorCode || songs.activeSong.code,
      cloudPatchId: songs.activeSong.cloudPatchId,
    });
  }, [songs.activeSong, editorTitle, editorCode]);

  const handlePublished = useCallback(
    (result: { cloudPatchId: string; visibility: 'public' | 'unlisted' | 'private' }) => {
      // Stamp the *original* target song, not whatever's currently
      // active — the user may have switched songs while the modal was
      // open. publishTarget captured songId at modal-open time.
      const targetId = publishTarget?.songId;
      if (targetId) songs.setSongCloudPatchId(targetId, result.cloudPatchId);
      setStatus({
        message: `Published as ${result.visibility} (id ${result.cloudPatchId.slice(0, 8)}…).`,
        level: 'ok',
      });
    },
    [publishTarget?.songId, songs],
  );

  const handleForked = useCallback(
    (params: { title: string; code: string; cloudPatchId: string }) => {
      cancelPreviewTimer();
      strudel.stop();
      // The worker has already created the fork in Supabase under
      // this user's account. Show the forked code in the editor
      // immediately; refetch the library in the background to bring
      // the new row in, then select it so the rail highlights it.
      setEditorTitle(params.title);
      setEditorCode(params.code);
      setSoloId(null);
      void songs
        .refetch()
        .then(() => songs.select(params.cloudPatchId))
        .catch(() => {
          // Non-fatal: the patch exists in Supabase regardless. Next
          // organic refetch will surface it.
        });
    },
    [songs, strudel, cancelPreviewTimer],
  );

  const handlePlayPatch = useCallback(
    async ({ title, code }: { title: string; code: string; cloudPatchId: string }) => {
      // Load the patch into the editor buffer and trigger Strudel —
      // no library mutation. Audition-style: anonymous users can play
      // community songs, and signed-in users keep their own library
      // intact. Forking is the deliberate save action.
      cancelPreviewTimer();
      setEditorTitle(title);
      setEditorCode(code);
      setSoloId(null);
      await playWithResolve(code);
    },
    [playWithResolve, cancelPreviewTimer],
  );

  // ─── Shared-song (`/s/<id>`) banner actions ──────────────────────
  // Play the shared draft that's already in the editor buffer, and
  // record an anonymous play against the source patch (fire-and-forget
  // — the audio is the primary action, the counter is decorative).
  const handleSharePlay = useCallback(() => {
    if (sharedBanner?.kind !== 'found') return;
    void handlePlay();
    recordPlayRequest(sharedBanner.patch.id).catch(() => {
      // Anonymous endpoint; silent failure is fine.
    });
  }, [sharedBanner, handlePlay]);

  const handleShareFork = useCallback(async () => {
    if (sharedBanner?.kind !== 'found') return;
    if (!session) {
      setStatus({ message: 'Sign in to fork this song.', level: 'warn' });
      return;
    }
    setSharedForking(true);
    try {
      const fork = await forkPatchToSong(sharedBanner.patch.id, {
        title: sharedBanner.patch.title,
        authorHandle: sharedBanner.patch.authorHandle,
      });
      handleForked({
        title: fork.title,
        code: fork.code,
        cloudPatchId: fork.cloudPatchId,
      });
      // The fork now lives in the user's library; the share banner has
      // done its job. Drop it and clean the URL back to root.
      setSharedBanner(null);
      window.history.replaceState(null, '', '/');
      setStatus({
        message: fork.lineageMissing
          ? `Forked "${sharedBanner.patch.title}" (lineage record skipped).`
          : `Forked "${sharedBanner.patch.title}" — opened in your library.`,
        level: fork.lineageMissing ? 'warn' : 'ok',
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ message: 'Fork failed: ' + msg, level: 'err' });
    } finally {
      setSharedForking(false);
    }
  }, [sharedBanner, session, handleForked]);

  const handleShareDismiss = useCallback(() => {
    setSharedBanner(null);
    // Restore a clean root URL so a reload / bookmark doesn't re-open
    // the banner. The editor draft stays put.
    window.history.replaceState(null, '', '/');
  }, []);

  // Boot-time route check: when the app opens on `/s/<id>`, load the
  // shared code into the editor as an unsaved draft and raise the
  // banner. prepareNew() nulls the active library id first so the
  // activeSong watcher (above) doesn't clobber the draft once the
  // user's library finishes loading. Runs once on mount.
  useEffect(() => {
    const id = parseShareId(window.location.pathname);
    if (!id) return;
    songs.prepareNew();
    setEditorTitle('');
    setEditorCode('');
    setSoloId(null);
    setWelcomeDemoPlaying(false);
    setView('editor');
    setSharedBanner({ kind: 'loading' });
    void getSharedPatch(id)
      .then((patch) => {
        if (!patch) {
          setSharedBanner({ kind: 'notfound' });
          setStatus({
            message: 'Shared song not found — it may be private or removed.',
            level: 'warn',
          });
          return;
        }
        setEditorTitle(patch.title);
        setEditorCode(patch.code);
        setSharedBanner({ kind: 'found', patch });
        setStatus({
          message: `Shared song "${patch.title}" — press Play to hear it.`,
          level: 'ok',
        });
      })
      .catch((e) => {
        setSharedBanner({ kind: 'notfound' });
        const msg = e instanceof Error ? e.message : String(e);
        setStatus({ message: 'Could not load shared song: ' + msg, level: 'err' });
      });
    // Mount-once: the URL is read a single time at boot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetSongs = useCallback(() => {
    const first = songs.resetToDefaults();
    if (first) {
      setEditorTitle(first.title);
      setEditorCode(first.code);
    }
    setSoloId(null);
    setStatus({ message: 'Library reset to defaults.', level: 'ok' });
  }, [songs]);

  const handleClearSongs = useCallback(() => {
    songs.clearAll();
    setEditorTitle('');
    setEditorCode('');
    setSoloId(null);
    setStatus({ message: 'All songs cleared.', level: 'ok' });
  }, [songs]);

  const handleResetSyncOffset = useCallback(() => {
    updateSyncOffset(0);
    setStatus({ message: 'Sync offset reset to 0.', level: 'ok' });
  }, [updateSyncOffset]);

  // Tempo scrubber commit — read the current buffer, run setTempo, push
  // the rewritten code back through the same channel a keystroke would.
  // Hot-swap picks it up and the next cycle starts at the new tempo.
  const handleTempoCommit = useCallback(
    (bpm: number) => {
      const cached = parseCodeCachedSync(editorCode);
      const parsed = cached ?? parseCode(editorCode);
      if (!parsed.ok) {
        setStatus({ message: 'Tempo change skipped: song has a syntax error.', level: 'warn' });
        return;
      }
      const next = setTempo(parsed.song, bpm);
      setEditorCode(generateCode(next));
      setStatus({ message: `Tempo → ${bpm} BPM.`, level: 'ok' });
    },
    [editorCode],
  );

  const handleDelete = useCallback(
    (id: string) => {
      const target = songs.songs.find((s) => s.id === id);
      if (!target) return;
      if (!confirm(`Delete "${target.title}"?`)) return;
      songs.deleteSong(id);
      if (songs.activeId === id) {
        setEditorTitle('');
        setEditorCode('');
      }
      setStatus({ message: 'Deleted.', level: '' });
    },
    [songs.songs, songs.deleteSong, songs.activeId],
  );

  const handleExport = useCallback(() => {
    const payload = JSON.stringify(songs.songs, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `strudel-songs-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setStatus({
      message: `Exported ${songs.songs.length} songs.`,
      level: 'ok',
    });
  }, [songs.songs]);

  const handleImportFile = useCallback(
    async (file: File) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(await file.text());
      } catch {
        setStatus({ message: 'Import failed: invalid JSON.', level: 'err' });
        return;
      }
      const result = songs.importSongs(parsed);
      if (!result) {
        setStatus({
          message: 'Import failed: expected an array of songs.',
          level: 'err',
        });
        return;
      }
      setStatus({
        message: `Imported: ${result.added} added, ${result.updated} updated.`,
        level: 'ok',
      });
    },
    [songs.importSongs],
  );

  // Keyboard shortcuts:
  //   Cmd/Ctrl+S           — save
  //   Cmd/Ctrl+Enter       — play (if engine is past boot)
  //   Cmd/Ctrl+K           — toggle the command palette
  //   Cmd/Ctrl+Shift+S     — toggle solo on the focused block
  //   Escape               — close the palette, or clear active solo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (mod && e.shiftKey && (e.key === 's' || e.key === 'S')) {
        const el = document.activeElement as HTMLElement | null;
        const blockEl = el?.closest<HTMLElement>('[data-block-id]');
        const targetId = blockEl?.dataset.blockId;
        if (!targetId) return;
        e.preventDefault();
        if (soloId === targetId) {
          setSoloId(null);
          return;
        }
        // Use songEdit.song, not a fresh parse — fresh parses mint new
        // ids that won't match `targetId` captured from the rendered
        // DOM (which reflects songEdit.song's ids).
        const song = songEdit.song;
        if (song && isSoloableId(song, targetId)) {
          setSoloId(targetId);
        }
        return;
      }
      if (mod && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
      if (mod && e.key === 'Enter') {
        e.preventDefault();
        if (
          strudel.phase !== 'idle' &&
          strudel.phase !== 'loading' &&
          strudel.phase !== 'error'
        ) {
          handlePlay();
        }
        return;
      }
      if (!mod && e.key === 'Escape' && soloId) {
        e.preventDefault();
        setSoloId(null);
        return;
      }
      // ? toggles Learn — gate against editable contexts so typing `?`
      // into CodeMirror, an input, a textarea, or any contentEditable
      // element doesn't open the modal.
      if (!mod && !e.repeat && e.key === '?') {
        const t = e.target as HTMLElement | null;
        if (t) {
          if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
          if (t.isContentEditable) return;
          if (t.closest('.cm-editor')) return;
        }
        e.preventDefault();
        setLearnOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleSave, handlePlay, strudel.phase, soloId, songEdit.song]);

  // React to solo changes. Solo behaves like a monophonic audition:
  //   - Set solo → play the isolated code (Strudel loops patterns
  //     natively, so the user hears the block on a continuous loop).
  //   - Clear solo → stop audio. Returning to the full song on clear
  //     would surprise a user who only wanted to audition a single
  //     block; if they want the full song, they hit Play.
  // Engine must be past boot before either branch can act.
  useEffect(() => {
    const ready =
      strudel.phase === 'ready' ||
      strudel.phase === 'playing' ||
      strudel.phase === 'paused';
    if (!ready) return;
    cancelPreviewTimer();
    if (soloId === null) {
      if (strudel.phase === 'playing' || strudel.phase === 'paused') {
        strudel.stop();
        setStatus({ message: 'Solo cleared — stopped.', level: '' });
      }
      return;
    }
    const resolved = resolvePlayCode();
    if (!resolved.ok) {
      setStatus({ message: resolved.reason, level: 'err' });
      return;
    }
    void (async () => {
      try {
        await playWithResolve(resolved.code);
        lastPlayedCodeRef.current = resolved.code;
        setStatus({
          message: 'Playing solo — edits are live.',
          level: 'ok',
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatus({ message: 'Play error: ' + msg, level: 'err' });
      }
    })();
    // `resolvePlayCode` closes over editorCode; we only want this
    // effect on solo transitions, not on every keystroke. The hot-
    // swap effect below handles keystroke-driven re-evaluation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloId]);

  // Hot-swap: while audio is playing, re-evaluate whenever the code or
  // solo selection changes. Strudel aligns pattern swaps to the next
  // cycle boundary, so the user hears edits land musically rather than
  // cutting out. Debounced so rapid-fire edits (held keys, slider
  // drags) don't thrash the evaluator.
  useEffect(() => {
    if (strudel.phase !== 'playing') return;
    if (previewId !== null) return;
    // Welcome-pane demo runs through the same engine but isn't backed
    // by `editorCode` — without this guard the hot-swap would silence
    // the demo 250ms in by re-evaluating to an empty editor buffer.
    if (welcomeDemoPlaying) return;
    const timer = window.setTimeout(() => {
      let next: PlayCode;
      try {
        next = resolvePlayCode();
      } catch {
        return;
      }
      if (!next.ok) {
        setStatus({ message: next.reason, level: 'warn' });
        return;
      }
      if (next.code === lastPlayedCodeRef.current) return;
      void (async () => {
        try {
          await playWithResolve(next.code);
          lastPlayedCodeRef.current = next.code;
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          setStatus({ message: 'Live update error: ' + msg, level: 'warn' });
        }
      })();
    }, HOT_SWAP_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [editorCode, soloId, strudel.phase, previewId, welcomeDemoPlaying, resolvePlayCode, playWithResolve]);

  // Surface runtime errors so they land in the status bar.
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      setStatus({
        message: 'Script error: ' + (e.message || String(e.error)),
        level: 'err',
      });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg = reason instanceof Error ? reason.message : String(reason);
      setStatus({ message: 'Promise error: ' + msg, level: 'err' });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // Memoized because App re-renders at the hap-poll rate (~12fps) now
  // that useHapData lives here. Recreating this object on every render
  // would defeat React.memo on LeftRack and force the SoundLibrary's
  // 40+ sound cards through reconciliation 12 times per second during
  // playback.
  const previewOps = useMemo(
    () => ({
      activeId: previewId,
      canPlay: canPreview,
      start: handleStartPreview,
      stop: handleStopPreview,
    }),
    [previewId, canPreview, handleStartPreview, handleStopPreview],
  );

  // Command palette — one flat list rebuilt when anything it closes
  // over changes. Stable reference identity isn't critical here
  // because CommandPalette's own memoisation is by query+commands;
  // rebuild cost is trivial (a dozen object literals).
  const commands = useMemo<Command[]>(() => {
    const playReady =
      strudel.phase === 'ready' ||
      strudel.phase === 'playing' ||
      strudel.phase === 'paused';
    const isPlaying = strudel.phase === 'playing';
    const isPaused = strudel.phase === 'paused';
    const soloActive = songEdit.soloOps.soloId !== null;
    const list: Command[] = [
      // Transport
      {
        id: 'transport.play',
        label: isPlaying ? 'Restart playback from cycle 0' : 'Play',
        description: 'Evaluate the current code and play.',
        section: 'Transport',
        shortcut: '⌘↵',
        keywords: ['start', 'evaluate'],
        disabled: !playReady,
        run: handlePlay,
      },
      {
        id: 'transport.pauseToggle',
        label: isPaused ? 'Resume' : 'Pause',
        description: isPaused
          ? 'Resume from the paused position.'
          : 'Pause playback without clearing the pattern.',
        section: 'Transport',
        keywords: ['suspend', 'hold'],
        disabled: !isPlaying && !isPaused,
        run: handlePauseToggle,
      },
      {
        id: 'transport.stop',
        label: 'Stop',
        description: 'Halt playback and silence audio.',
        section: 'Transport',
        keywords: ['silence', 'end'],
        run: handleStop,
      },
      {
        id: 'transport.save',
        label: 'Save song',
        description: dirty ? 'Write unsaved changes to storage.' : 'Already saved.',
        section: 'Transport',
        shortcut: '⌘S',
        keywords: ['persist', 'store'],
        run: handleSave,
      },
      // Navigation
      {
        id: 'nav.home',
        label: 'Go home',
        description: 'Return to the home page (recent, favourites, lessons).',
        section: 'Navigation',
        keywords: ['home', 'welcome', 'landing', 'start'],
        run: goHome,
      },
      {
        id: 'nav.new',
        label: 'New song',
        description: 'Clear the buffer and start a fresh song.',
        section: 'Navigation',
        keywords: ['empty', 'fresh', 'clear'],
        run: handleNew,
      },
      {
        id: 'nav.settings',
        label: 'Open settings',
        description: 'Recovery + diagnostics panel.',
        section: 'Navigation',
        keywords: ['recovery', 'reset', 'diagnostics', 'gear'],
        run: handleOpenSettings,
      },
      {
        id: 'nav.learn',
        label: 'Open Learn',
        description: 'Tutorials on tempo, key, structure, mood.',
        section: 'Navigation',
        shortcut: '?',
        keywords: ['tutorial', 'lesson', 'guide', 'help', 'docs', 'learn'],
        run: handleOpenLearn,
      },
      {
        id: 'nav.openRack',
        label: 'Open rack',
        description: 'Reveal the songs + sound vault rail (mobile).',
        section: 'Navigation',
        keywords: ['sidebar', 'songs', 'sound vault', 'library'],
        run: handleOpenRack,
      },
      // Editor
      {
        id: 'editor.clearSolo',
        label: 'Clear block solo',
        description: 'Un-solo the currently isolated block.',
        section: 'Editor',
        shortcut: 'Esc',
        keywords: ['solo', 'isolate', 'clear'],
        disabled: !soloActive,
        run: () => setSoloId(null),
      },
      {
        id: 'editor.undo',
        label: 'Undo block edit',
        section: 'Editor',
        shortcut: '⌘Z',
        keywords: ['back'],
        disabled: !songEdit.ops.canUndo,
        run: songEdit.ops.undo,
      },
      {
        id: 'editor.redo',
        label: 'Redo block edit',
        section: 'Editor',
        shortcut: '⌘⇧Z',
        keywords: ['forward'],
        disabled: !songEdit.ops.canRedo,
        run: songEdit.ops.redo,
      },
      // Library
      {
        id: 'lib.reset',
        label: 'Reset songs to defaults',
        description: 'Wipe the saved library and restore the starter songs.',
        section: 'Library',
        keywords: ['seed', 'defaults', 'starter'],
        run: handleResetSongs,
      },
      {
        id: 'lib.clear',
        label: 'Clear all songs',
        description: 'Leave the library empty.',
        section: 'Library',
        keywords: ['wipe', 'delete'],
        run: handleClearSongs,
      },
      {
        id: 'lib.resetSync',
        label: 'Reset sync offset',
        description: 'Reset latency calibration to 0 ms.',
        section: 'Library',
        keywords: ['latency', 'offset', 'calibration'],
        run: handleResetSyncOffset,
      },
      {
        id: 'lib.export',
        label: 'Export songs as JSON',
        section: 'Library',
        keywords: ['download', 'backup'],
        run: handleExport,
      },
      {
        id: 'lib.publish',
        label: 'Publish current song to community',
        description: 'Push the active song to the cloud as public, unlisted, or private.',
        section: 'Library',
        keywords: ['share', 'cloud', 'community', 'patch'],
        disabled: !songs.activeSong,
        run: handleOpenPublish,
      },
      // Recording
      {
        id: 'rec.toggle',
        label:
          recordings.recorderState === 'recording' || recordings.recorderState === 'paused'
            ? 'Stop recording'
            : 'Start recording',
        description:
          recordings.recorderState === 'recording' || recordings.recorderState === 'paused'
            ? 'Stop the current take and add it to the Takes panel.'
            : 'Capture the audio output to a new take.',
        section: 'Recording',
        keywords: ['record', 'capture', 'audio'],
        disabled: !hasRecordingTap() &&
          recordings.recorderState !== 'recording' &&
          recordings.recorderState !== 'paused',
        run: handleToggleRecord,
      },
      {
        id: 'rec.pause',
        label: recordings.recorderState === 'paused' ? 'Resume recording' : 'Pause recording',
        section: 'Recording',
        keywords: ['record', 'pause', 'resume'],
        disabled:
          recordings.recorderState !== 'recording' && recordings.recorderState !== 'paused',
        run: () => {
          if (recordings.recorderState === 'paused') recordings.resume();
          else recordings.pause();
        },
      },
      {
        id: 'rec.cancel',
        label: 'Cancel recording (discard)',
        description: 'Stop the in-flight recording without saving a take.',
        section: 'Recording',
        keywords: ['discard', 'abort', 'trash'],
        disabled:
          recordings.recorderState !== 'recording' && recordings.recorderState !== 'paused',
        run: () => {
          recordings.cancel();
          setStatus({ message: 'Recording cancelled — take discarded.', level: '' });
        },
      },
      {
        id: 'rec.openTakes',
        label: 'Open takes',
        description: `View and download the ${recordings.takes.length} captured takes.`,
        section: 'Recording',
        keywords: ['takes', 'download', 'wav', 'export'],
        disabled: recordings.takes.length === 0,
        run: handleOpenTakes,
      },
      {
        id: 'rec.downloadLastWav',
        label: 'Download last take as WAV',
        section: 'Recording',
        keywords: ['download', 'wav', 'export', 'last'],
        disabled: recordings.takes.length === 0,
        run: () => {
          const latest = recordings.takes[0];
          if (latest) void handleDownloadTake(latest.id, 'wav');
        },
      },
      {
        id: 'rec.clearTakes',
        label: 'Clear all takes',
        section: 'Recording',
        keywords: ['wipe', 'delete', 'clear'],
        disabled: recordings.takes.length === 0,
        run: handleClearTakes,
      },
    ];
    // Generative transforms — one Command per (transform × applicable
    // voice) in the current song. Filtered against the parsed Song so
    // the palette only shows commands that will actually do something.
    list.push(...buildTransformCommands(songEdit.song, songEdit.ops));
    list.push(...buildGridCommands(songEdit.song, setGridTarget));
    return list;
  }, [
    strudel.phase,
    dirty,
    songEdit.song,
    songEdit.ops,
    songEdit.soloOps.soloId,
    handlePlay,
    handlePauseToggle,
    handleStop,
    handleSave,
    handleNew,
    handleOpenSettings,
    handleOpenLearn,
    handleOpenRack,
    goHome,
    handleResetSongs,
    handleClearSongs,
    handleResetSyncOffset,
    handleExport,
    handleOpenPublish,
    songs.activeSong,
    recordings,
    handleToggleRecord,
    handleOpenTakes,
    handleDownloadTake,
    handleClearTakes,
  ]);

  // AgentBridge — refs keep the bridge's reads live without rebuilding
  // the bridge (and the running stream) on every state change.
  const agentCodeRef = useRef<string>(editorCode);
  agentCodeRef.current = editorCode;
  const agentSoloRef = useRef<BlockId | null>(soloId);
  agentSoloRef.current = soloId;
  const agentPhaseRef = useRef(strudel.phase);
  agentPhaseRef.current = strudel.phase;
  const agentSongRef = useRef<Song | null>(songEdit.song);
  agentSongRef.current = songEdit.song;
  // handlePlay / handleStop are useCallbacks whose identity changes
  // whenever their deps (soloId, strudel.play, resolvePlayCode) change.
  // The bridge is captured by a running sendTurn at call time, so if we
  // closed over them directly a mid-stream bridge.play() could fire a
  // stale closure with stale solo state. Ref-through matches what we
  // do for every other state field above.
  const handlePlayRef = useRef(handlePlay);
  handlePlayRef.current = handlePlay;
  const handleStopRef = useRef(handleStop);
  handleStopRef.current = handleStop;

  const agentBridge = useMemo<AgentBridge>(
    () => ({
      getSong: () => agentSongRef.current ?? { statements: [] },
      getEditorCode: () => agentCodeRef.current,
      getTransportState: (): TransportSnapshot => {
        const phase = agentPhaseRef.current;
        const uiPhase: TransportSnapshot['phase'] =
          phase === 'idle' || phase === 'loading' || phase === 'error'
            ? phase
            : phase === 'playing'
              ? 'playing'
              : 'ready';
        const cps = hapRefs.cps.current;
        return {
          playing: phase === 'playing',
          phase: uiPhase,
          cpm: cps != null ? Math.round(cps * 60) : null,
          cps: cps ?? null,
          cycle: hapRefs.cycle.current ?? null,
          soloId: agentSoloRef.current,
        };
      },
      getSampleBanks: () => {
        const song = agentSongRef.current;
        if (!song) return [];
        const banks: string[] = [];
        for (const stmt of song.statements) {
          if (stmt.kind === 'samples') banks.push(stmt.source);
        }
        return banks;
      },
      applyRecipe: (recipe) => {
        try {
          songEdit.ops.update(recipe);
          return { ok: true };
        } catch (e) {
          return {
            ok: false,
            reason: e instanceof Error ? e.message : String(e),
          };
        }
      },
      play: () => {
        const phase = agentPhaseRef.current;
        if (phase === 'idle' || phase === 'loading' || phase === 'error') {
          return {
            ok: false,
            reason: `engine not ready (phase: ${phase}) — user must press Play once to boot it`,
          };
        }
        try {
          handlePlayRef.current();
          return { ok: true };
        } catch (e) {
          return {
            ok: false,
            reason: e instanceof Error ? e.message : String(e),
          };
        }
      },
      stop: () => {
        try {
          handleStopRef.current();
          return { ok: true };
        } catch (e) {
          return {
            ok: false,
            reason: e instanceof Error ? e.message : String(e),
          };
        }
      },
      setSolo: (blockId) => {
        const song = agentSongRef.current;
        if (blockId !== null) {
          if (!song) return { ok: false, reason: 'song not parsed' };
          if (!isSoloableId(song, blockId)) {
            return {
              ok: false,
              reason: `block ${blockId} is not soloable (must be a voice or named ref)`,
            };
          }
        }
        setSoloId(blockId);
        return { ok: true };
      },
      flashChange: (from, to) => {
        const view = viewRef.current;
        if (!view) return;
        flashViewRange(view, from, to);
      },
      getCps: () => hapRefs.cps.current ?? null,
      getRecentHaps: () => {
        // Hap stream carries ~last 2-3 cycles of events. Return just
        // the onset times — the panel bins them for display.
        // VisualHap exposes the onset as `begin` (cycles on the
        // transport clock); the raw Strudel Hap.whole has been
        // unwrapped already in useHaps' normalization step.
        const haps = hapRefs.haps.current;
        if (haps.length === 0) return [];
        const out: number[] = [];
        for (const h of haps) {
          if (Number.isFinite(h.begin)) out.push(h.begin);
        }
        return out;
      },
    }),
    // Bridge is stable across handler changes — play/stop go through refs.
    [hapRefs, songEdit.ops],
  );

  return (
    <SampleStoreProvider storage={getSampleStorage()}>
    <UnsavedGuardProvider value={{ guardThen }}>
    <EditContext.Provider value={songEdit.ops}>
      <SoloContext.Provider value={songEdit.soloOps}>
        <div className="h-full flex flex-col">
          {/* Three-column shell on desktop:
                LeftRack | center column | ChatPanel
              Center column stacks TransportDeck / PerformanceStrip /
              SourcePane. The strip only takes layout while playing.
              ChatPanel is hidden on mobile for now — drawer pattern
              will arrive when small-screen UX is revisited. */}
          <div className="flex-1 flex min-h-0 min-w-0">
            <ResizableSideRail
              side="left"
              storageKey="strudel-harness:rack-width"
              defaultWidth={320}
              minWidth={260}
              maxWidth={560}
              ariaLabel="Resize library rail"
              mobileBehavior="pass-through"
              collapsed={rackCollapsed}
            >
              <LeftRack
                songs={songs.songs}
                activeId={songs.activeId}
                onSelect={handleHomeOpenSong}
                onDelete={handleDelete}
                onToggleFavourite={songs.toggleFavourite}
                onNew={handleNew}
                onExport={handleExport}
                onImportFile={handleImportFile}
                onOpenSettings={handleOpenSettings}
                onOpenForge={handleOpenForge}
                preview={previewOps}
                mobileOpen={rackOpen}
                onMobileClose={handleCloseRack}
                collapsed={rackCollapsed}
                onToggleCollapsed={handleToggleRackCollapsed}
                expandedSection={expandedSection}
                onSwitchSection={handleSwitchSection}
                forgeActive={forgeActive}
                onForgeBack={handleForgeBack}
                onStatus={onStatus}
                onPublishActive={handleOpenPublish}
                canPublish={!!songs.activeSong}
                onForked={handleForked}
                onPlayPatch={handlePlayPatch}
                onOpenLearn={handleOpenLearn}
                songsPulse={songsPulse}
              />
            </ResizableSideRail>

            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <TransportDeck
                title={editorTitle}
                onTitleChange={setEditorTitle}
                onPlay={handlePlay}
                onPauseToggle={handlePauseToggle}
                onRestart={handleRestart}
                onStop={handleStop}
                onSave={handleSave}
                phase={strudel.phase}
                nowRef={nowRef}
                cycleRef={hapRefs.cycle}
                cpsRef={hapRefs.cps}
                startedRef={hapRefs.started}
                onTempoCommit={handleTempoCommit}
                dirty={dirty}
                onOpenRack={handleOpenRack}
                onGoHome={goHome}
                recorderState={recordings.recorderState}
                recordDurationMs={recordings.currentDurationMs}
                onToggleRecord={handleToggleRecord}
                takesCount={recordings.takes.length}
                onOpenTakes={handleOpenTakes}
                recordingAvailable={hasRecordingTap()}
                shareUrl={
                  songs.activeSong?.cloudPatchId
                    ? buildShareUrl(songs.activeSong.cloudPatchId)
                    : null
                }
                onCopyStatus={onStatus}
              />

              {/* Performance strip — slides into view under the
                  transport buttons only while the engine is
                  transmitting. Replaces the old bottom LiveDeck; idle
                  collapse means the editor reclaims the full column
                  height when not playing. */}
              <PerformanceStrip
                refs={hapRefs}
                structural={hapStructural}
                nowRef={nowRef}
                syncOffsetMs={syncOffsetMs}
                onSyncChange={updateSyncOffset}
              />

              {/* Shared-song landing banner — only mounted when the app
                  opened on a `/s/<id>` link. Sits above the code so the
                  attribution + Play/Fork/Copy actions are the first
                  thing a visitor sees. */}
              {sharedBanner && (
                <SharedSongBanner
                  state={sharedBanner}
                  signedIn={!!session}
                  forking={sharedForking}
                  onPlay={handleSharePlay}
                  onFork={() => void handleShareFork()}
                  onCopyStatus={onStatus}
                  onDismiss={handleShareDismiss}
                />
              )}

              <div className="flex-1 flex flex-col min-h-0 min-w-0">
                {/* Home is a navigable destination, not a one-shot
                    landing. Cold start lands here; the TransportDeck
                    wordmark + Cmd-K "Go home" return to it from the
                    editor at any time. */}
                <div className="relative flex-1 min-h-0 min-w-0 flex flex-col">
                  {view === 'editor' ? (
                    <SourcePane
                      code={editorCode}
                      onChange={setEditorCode}
                      setView={handleSetView}
                      onSelectionChange={setEditorSelection}
                    />
                  ) : (
                    <WelcomePane
                      phase={strudel.phase}
                      demoPlaying={welcomeDemoPlaying}
                      firstRun={!onboarded}
                      onJumpIn={handleWelcomeJumpIn}
                      onHearDemo={handleWelcomeHearDemo}
                      onStopDemo={handleWelcomeStopDemo}
                      onOpenDemoInEditor={handleWelcomeOpenDemo}
                      onStartBlank={handleWelcomeStartBlank}
                      onBrowseCommunity={handleWelcomeBrowseCommunity}
                      recentSongs={recentSongs}
                      favouriteSongs={favouriteSongs}
                      lessonCards={LESSON_CARDS}
                      onOpenSong={handleHomeOpenSong}
                      onToggleFavourite={songs.toggleFavourite}
                      onOpenLesson={handleHomeOpenLesson}
                    />
                  )}
                  {/* Coachmark tour overlays the editor column; stays
                      mounted (step state intact) if the user detours
                      via Home mid-tour. */}
                  {tourActive && (
                    <OnboardingTour
                      visible={view === 'editor'}
                      code={editorCode}
                      demoCode={DEMO_CODE}
                      playing={strudel.phase === 'playing'}
                      view={editorView}
                      onSeedAgentPrompt={handleSeedAgentPrompt}
                      onFinished={handleTourFinished}
                    />
                  )}
                </div>
              </div>
            </div>

            <ResizableSideRail
              side="right"
              storageKey="strudel-harness:chat-rail-width"
              defaultWidth={420}
              minWidth={300}
              maxWidth={720}
              ariaLabel="Resize chat panel"
            >
              <ChatPanel
                onOpenSettings={handleOpenSettings}
                bridge={agentBridge}
                selection={editorSelection}
                seededPrompt={agentSeed}
              />
            </ResizableSideRail>
          </div>

          {/* Mobile pedalboard — Eurorack-style footswitch row that
              owns transport on phones. The TransportDeck's inline
              toolbar is hidden below md, so this is the only way to
              Play/Stop/Rec/Save on a phone. Renders nothing at md+. */}
          <MobilePedalboard
            phase={strudel.phase}
            onPlay={handlePlay}
            onPauseToggle={handlePauseToggle}
            onRestart={handleRestart}
            onStop={handleStop}
            onSave={handleSave}
            dirty={dirty}
            recorderState={recordings.recorderState}
            recordDurationMs={recordings.currentDurationMs}
            onToggleRecord={handleToggleRecord}
            recordingAvailable={hasRecordingTap()}
            takesCount={recordings.takes.length}
            onOpenTakes={handleOpenTakes}
          />

          <StatusBar message={status.message} level={status.level} />
        </div>

        {/* Silent PWA refresh — registers the SW, polls for new
            builds, and reloads the page on the next tab-hide event
            so a fresh bundle lands without interrupting the user. */}
        <PwaUpdater />

        <CommandPalette
          open={paletteOpen}
          onClose={handleClosePalette}
          commands={commands}
        />

        {/* Lazy modals. Each chunk only fetches when the modal first
            opens — closing then re-opening uses the cached chunk.
            Suspense fallback is null so the close→open round trip on
            slow connections briefly shows nothing rather than a
            spinner that would feel out of place over the workspace. */}
        <Suspense fallback={null}>
          {settingsOpen && (
            <SettingsModal
              open
              onClose={handleCloseSettings}
              strudelPhase={strudel.phase}
              onResetSongs={handleResetSongs}
              onClearSongs={handleClearSongs}
              onResetSyncOffset={handleResetSyncOffset}
              onClearTakes={recordings.clearAll}
              takesCount={recordings.takes.length}
              autosaveEnabled={autosave.settings.enabled}
              autosaveIdleMs={autosave.settings.idleMs}
              onAutosaveEnabledChange={autosave.setEnabled}
              onAutosaveIdleMsChange={autosave.setIdleMs}
            />
          )}

          {learnOpen && (
            <LearnModal
              open
              onClose={handleCloseLearn}
              previewSource={{
                id: previewId,
                canPreview,
                onPlay: handleStartPreview,
                onStop: handleStopPreview,
              }}
              onSaveAsSong={handleLearnSaveAsSong}
              initialLessonId={pendingLessonId}
            />
          )}

          {takesOpen && (
            <TakesModal
              open
              takes={recordings.takes}
              onClose={handleCloseTakes}
              onRename={recordings.renameTake}
              onRemove={recordings.removeTake}
              onDownload={handleDownloadTake}
              onClearAll={handleClearTakes}
            />
          )}

          {gridTarget !== null && (
            <GridEditorModalLazy
              song={songEdit.song}
              target={gridTarget}
              onClose={handleCloseGrid}
            />
          )}

          {publishTarget !== null && (
            <PublishSongModal
              target={publishTarget}
              onClose={handleClosePublish}
              onPublished={handlePublished}
            />
          )}

          {recoveryMode && (
            <RecoverPasswordModal
              open
              email={session?.user.email ?? null}
              onDismiss={() => void exitRecoveryMode()}
            />
          )}
        </Suspense>

        <UnsavedChangesModal prompt={unsavedPrompt} />

        <DropOverlay onStatus={onStatus} />
      </SoloContext.Provider>
    </EditContext.Provider>
    </UnsavedGuardProvider>
    </SampleStoreProvider>
  );
}
